import { useContext, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useApi } from '@backstage/core-plugin-api';

import { trackingApiRef } from '../../apis';
import { TrackingContext } from '../../contexts';
import { ElementId, TrackingData } from '../../types';
import { useElementPath } from '../useElementPath';
import { usePageView } from '../usePageView';

type UseImpressionTrackerOptions = {
  elementId: ElementId;
  data?: TrackingData;
};

export const useImpressionTracker = ({
  elementId,
  data,
}: UseImpressionTrackerOptions) => {
  const tracking = useApi(trackingApiRef);
  const pageView = usePageView();
  const elementPath = useElementPath(elementId);
  const { data: contextData } = useContext(TrackingContext);
  const mergedData = useMemo(
    () => (data ? { ...contextData, ...data } : contextData),
    [contextData, data],
  );

  const { ref, inView } = useInView({
    root: null,
    rootMargin: '0px',
    // 50% of the element must be visible to trigger an impression.
    threshold: 0.5,
  });

  // We only track one impression per mounted element.
  const tracked = useRef(false);
  const warnedRef = useRef(false);

  // Reset when the element path changes so a new impression can fire.
  useEffect(() => {
    tracked.current = false;
  }, [elementPath]);

  useEffect(() => {
    if (inView && !tracked.current) {
      if (process.env.NODE_ENV === 'development' && !warnedRef.current) {
        if (pageView.pageId === null) {
          // eslint-disable-next-line no-console
          console.warn(
            `useImpressionTracker("${elementId}") fired outside a <PageImpression>. ` +
              'Events will not have a pageViewId.',
          );
          warnedRef.current = true;
        }
      }
      tracking.trackImpression({ elementPath, data: mergedData });
      tracked.current = true;
    }
  }, [inView, elementPath, mergedData, tracking, pageView.pageId, elementId]);

  return ref;
};
